import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import HierarchicalOpalGrid from '../DenaliDataPipeline/report/HierarchicalOpalGrid'
import { useCollapsibleColumns } from '../../../hooks'
import { useFormattingContext } from '../FormattingProvider/FormattingContext'
import { useHideIfNoDataWhen } from '../HideIfNoData'
import BlueprintTableToolbar from './BlueprintTableToolbar'
import useBlueprintData from './hooks/useBlueprintData'
import useExhaustiveBlueprintData, { MAX_ROWS } from './hooks/useExhaustiveBlueprintData'
import { LATCH_LATEST } from './constants'
import useBlueprintColumnDefs, { mapDefaultSort } from './hooks/useBlueprintColumnDefs'
import useColumnConfig from './hooks/useColumnConfig'
import useBlueprintPivotTreeData from './hooks/useBlueprintPivotTreeData'
import GridColumnConfigModal from './GridColumnConfigModal'
import BlueprintExportButton from './BlueprintExportButton'
import useStyleVariant from './hooks/useStyleVariant'

const PAGE_STATE = { pageIndex: 0, pageSize: MAX_ROWS }

function getSubRows (row) {
  return row.subRows
}

/**
 * Builds effective columns by taking the static column defs and splicing
 * dynamic pivot columns in place of the placeholder column (header: '*').
 * If no placeholder exists, pivot columns are appended before the last column.
 */
function makeCellFormatter (formatter, formatString) {
  if (!formatString) return undefined
  return (props) => {
    const val = props.getValue()
    if (val == null) return ''
    return formatter(val, formatString)
  }
}

function usePivotEffectiveColumns (staticColumns, pivotColumns, pivotConfig, formatter, hidePivot) {
  return useMemo(() => {
    if (!staticColumns?.length) return []

    if (hidePivot) {
      return staticColumns.filter(c => c.header !== '*')
    }

    const pivotFormat = pivotConfig?.valueFormat || null
    const result = [...staticColumns]

    const expandIndex = result.findIndex(c => c.header === '*')

    const mappedPivotCols = pivotColumns.map(pc => {
      const col = {
        accessorKey: pc.accessor,
        header: typeof pc.header === 'function' ? pc.header() : pc.header,
        align: 'right',
        defaultShown: true,
        enableSorting: true,
        size: 120
      }

      const cellFn = makeCellFormatter(formatter, pivotFormat)
      if (cellFn) {
        col.cell = cellFn
      }

      if (pc.isGroup) {
        col.isGroup = true
        col.groupId = pc.groupId
        col.children = pc.children
        col.isCollapsible = true
      }
      if (pc.parentGroupId) {
        col.parentGroupId = pc.parentGroupId
        col.isGroup = false
      }

      return col
    })

    const syntheticCols = []

    if (pivotConfig?.showTotal !== false) {
      const totalCol = {
        accessorKey: '_total',
        header: 'Total',
        align: 'right',
        defaultShown: true,
        enableSorting: true,
        size: 130
      }
      const totalCellFn = makeCellFormatter(formatter, pivotFormat)
      if (totalCellFn) {
        totalCol.cell = totalCellFn
      }
      syntheticCols.push(totalCol)
    }

    if (pivotConfig?.showAllocation) {
      syntheticCols.push({
        accessorKey: '_allocation',
        header: 'Allocation',
        align: 'right',
        defaultShown: true,
        enableSorting: true,
        size: 110,
        cell: (props) => {
          const val = props.getValue()
          if (val == null) return ''
          return `${(val * 100).toFixed(1)}%`
        }
      })
    }

    if (expandIndex >= 0) {
      result.splice(expandIndex, 1, ...mappedPivotCols, ...syntheticCols)
    } else {
      const insertIdx = Math.max(result.length - 1, 0)
      result.splice(insertIdx, 0, ...mappedPivotCols, ...syntheticCols)
    }

    return result
  }, [staticColumns, pivotColumns, pivotConfig, formatter, hidePivot])
}

function BlueprintHierarchicalPivotTable ({
  blueprintCode,
  config,
  latch,
  displayName,
  parameters,
  scopePredicates,
  definition
}) {
  const {
    purposeCode,
    footerPurposeCode,
    footerColumnMapping,
    defaultSort: rawDefaultSort,
    hierarchy,
    pivot: pivotConfig
  } = config

  const effectiveParameters = useMemo(() => {
    if (!latch) return parameters
    return { ...parameters, [latch.parameterName]: LATCH_LATEST }
  }, [parameters, latch])

  const hasFooterPurpose = !!footerPurposeCode && footerPurposeCode !== purposeCode
  const hasColumnConfig = !!config.enableColumnConfig
  const hasSearch = !!config.enableSearch
  const [globalFilter, setGlobalFilter] = useState('')

  const PIVOT_VIRTUAL = '__pivot__'

  const columnsForConfig = useMemo(() => {
    if (!hasColumnConfig) return config.columns
    return config.columns.map(col =>
      col.name === '*'
        ? { ...col, name: PIVOT_VIRTUAL, displayName: 'Pivoted Columns' }
        : col
    )
  }, [config.columns, hasColumnConfig])

  const { mergedColumns: rawMerged, handleColumnConfigSave: rawSave } = useColumnConfig(
    columnsForConfig, config.localStorageKey
  )
  const [configOpen, setConfigOpen] = useState(false)

  const pivotHidden = useMemo(() => {
    if (!hasColumnConfig) return false
    const entry = rawMerged.find(c => c.name === PIVOT_VIRTUAL)
    return entry?.hidden ?? false
  }, [hasColumnConfig, rawMerged])

  const columnsForDefs = useMemo(() => {
    if (!hasColumnConfig) return config.columns
    return rawMerged.map(col =>
      col.name === PIVOT_VIRTUAL
        ? { ...col, name: '*', displayName: undefined }
        : col
    )
  }, [hasColumnConfig, rawMerged, config.columns])

  const { data: mainData, isLoading: mainLoading, truncated } = useExhaustiveBlueprintData(
    blueprintCode,
    purposeCode,
    { parameters: effectiveParameters, pageSize: 1000, scopePredicates, definition }
  )

  const { data: footerData, isLoading: footerLoading } = useBlueprintData(
    blueprintCode,
    hasFooterPurpose ? footerPurposeCode : null,
    { parameters: effectiveParameters, pageSize: 1000, scopePredicates, definition }
  )

  const { columns: staticColumns, includeFooter } = useBlueprintColumnDefs(
    columnsForDefs,
    footerData,
    footerColumnMapping
  )

  const { formatter } = useFormattingContext()

  const {
    treeData,
    pivotColumns,
    expandState,
    setExpandState
  } = useBlueprintPivotTreeData(mainData, hierarchy, pivotConfig)

  const effectiveColumns = usePivotEffectiveColumns(
    staticColumns, pivotColumns, pivotConfig, formatter, pivotHidden
  )

  const {
    visibleColumns,
    collapsedGroups,
    toggleGroup
  } = useCollapsibleColumns(
    effectiveColumns,
    false,
    pivotConfig?.enableColumnGrouping ?? false
  )

  const defaultSort = useMemo(() => mapDefaultSort(rawDefaultSort), [rawDefaultSort])
  const [sortState, setSortState] = useState(defaultSort)

  useEffect(() => {
    setSortState(defaultSort)
  }, [defaultSort])

  const displayColumns = useMemo(() => {
    const cols = pivotConfig?.enableColumnGrouping ? visibleColumns : effectiveColumns
    return cols.filter(col => col.defaultShown ?? true)
  }, [pivotConfig, visibleColumns, effectiveColumns])

  const variant = useStyleVariant('Hierarchical Pivot Table', config)

  const isLoading = mainLoading || (hasFooterPurpose && footerLoading)
  useHideIfNoDataWhen(() => isLoading || !mainData?.length, [isLoading, mainData])
  const isFiltering = hasSearch && globalFilter.length > 0
  const effectiveExpandState = isFiltering ? true : expandState
  const showToolbar = !!config.export?.enabled || hasColumnConfig || hasSearch

  const truncationWarning = truncated
    ? (
      <div className='opal-callout opal-callout-warning font-size-sm'>
        Showing the first {mainData.length.toLocaleString()} rows. The full dataset exceeds the display limit.
      </div>
    )
    : null

  const table = (
    <HierarchicalOpalGrid
      {...variant.props}
      className={variant.tableClassName}
      style={variant.styles}
      data={treeData}
      columns={displayColumns}
      isFetching={isLoading}
      pageState={PAGE_STATE}
      onPageChange={() => {}}
      expandState={effectiveExpandState}
      onExpandStateChange={setExpandState}
      sortState={sortState}
      onSortChange={setSortState}
      getSubRows={getSubRows}
      includeFooter={includeFooter}
      hidePaging
      collapsedGroups={collapsedGroups}
      onToggleColumnGroup={toggleGroup}
      enableFiltering={hasSearch}
      globalFilter={hasSearch ? globalFilter : undefined}
      onGlobalFilterChange={hasSearch ? setGlobalFilter : undefined}
    />
  )

  if (!showToolbar && !displayName && !truncated) {
    return variant.className
      ? <div className={variant.className}>{table}</div>
      : table
  }

  return (
    <BlueprintTableToolbar
      className={variant.className}
      displayName={displayName}
      hasSearch={hasSearch}
      globalFilter={globalFilter}
      onGlobalFilterChange={setGlobalFilter}
      showExport={!!config.export?.enabled}
      exportButton={
        <BlueprintExportButton
          blueprintCode={blueprintCode}
          config={config}
          purposeCode={purposeCode}
          parameters={effectiveParameters}
          scopePredicates={scopePredicates}
        />
      }
      hasColumnConfig={hasColumnConfig}
      onConfigClick={() => setConfigOpen(true)}
      configModal={
        hasColumnConfig
          ? (
            <GridColumnConfigModal
              open={configOpen}
              onClose={() => setConfigOpen(false)}
              columns={rawMerged}
              defaultColumns={columnsForConfig}
              onSave={rawSave}
            />
          )
          : null
      }
    >
      {truncationWarning}
      {table}
    </BlueprintTableToolbar>
  )
}

BlueprintHierarchicalPivotTable.propTypes = {
  blueprintCode: PropTypes.string.isRequired,
  config: PropTypes.shape({
    purposeCode: PropTypes.string.isRequired,
    columns: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      displayName: PropTypes.string,
      hidden: PropTypes.bool,
      exclude: PropTypes.bool,
      sortable: PropTypes.bool,
      filterable: PropTypes.bool,
      formatString: PropTypes.string
    })),
    hierarchy: PropTypes.shape({
      levels: PropTypes.arrayOf(PropTypes.shape({
        groupBy: PropTypes.arrayOf(PropTypes.string).isRequired,
        labelAccessor: PropTypes.string.isRequired
      })).isRequired,
      defaultExpandDepth: PropTypes.number,
      labelColumn: PropTypes.string,
      leafLabelAccessor: PropTypes.string
    }).isRequired,
    pivot: PropTypes.shape({
      pivotColumn: PropTypes.string.isRequired,
      valueColumn: PropTypes.string.isRequired,
      aggregateFunction: PropTypes.oneOf(['sum', 'avg', 'min', 'max', 'count', 'first', 'last']),
      showTotal: PropTypes.bool,
      showAllocation: PropTypes.bool,
      sortPivotColumns: PropTypes.oneOf(['alpha', 'ordinal', 'value']),
      ordinalColumn: PropTypes.string,
      enableColumnGrouping: PropTypes.bool,
      groupColumn: PropTypes.string,
      valueFormat: PropTypes.string
    }).isRequired,
    footerPurposeCode: PropTypes.string,
    footerColumnMapping: PropTypes.arrayOf(PropTypes.shape({
      mainColumn: PropTypes.string,
      footerColumn: PropTypes.string
    })),
    defaultSort: PropTypes.arrayOf(PropTypes.shape({
      field: PropTypes.string,
      dir: PropTypes.string
    })),
    enableColumnConfig: PropTypes.bool,
    enableSearch: PropTypes.bool,
    localStorageKey: PropTypes.string,
    export: PropTypes.shape({
      enabled: PropTypes.bool,
      formats: PropTypes.arrayOf(PropTypes.string),
      defaultFileName: PropTypes.string
    })
  }).isRequired,
  latch: PropTypes.object,
  displayName: PropTypes.string,
  parameters: PropTypes.object,
  scopePredicates: PropTypes.arrayOf(PropTypes.object),
  definition: PropTypes.object
}

BlueprintHierarchicalPivotTable.defaultProps = {
  latch: null,
  parameters: {}
}

export default BlueprintHierarchicalPivotTable
